// Shared speech player: neural TTS (backend /api/tts, Gemini) with
// browser-voice fallback, single-playback registry, media-session transport,
// and the iOS autoplay bless used by the Claude panel's hands-free voice
// mode. Extracted verbatim from ClaudePanel.tsx so DocsPanel's Listen button
// and voice mode share one engine. React-component-free on purpose (the
// ListenButton component lives in components/ListenButton.tsx) so this file
// can export hooks/functions without tripping react-refresh lint.
import { useState, useEffect, useCallback, useRef } from 'react'
import { splitIntoSpeechChunks } from './speech'
import { resolveApiBase } from './mountBase'
import { useSettingsOptional } from '../hooks/useSettings'

// ─── Text-to-Speech ─────────────────────────────────────────────────────────
// Primary path: high-quality neural voice from the backend /api/tts (Gemini).
// Fallback: the browser's built-in speechSynthesis if the endpoint is missing,
// rate-limited, or errors. A single sound plays at a time, so a module-level
// registry lets every ListenButton reflect whether *it* is the one speaking;
// starting playback elsewhere stops the previous one.

const SPEECH_SYNTH_SUPPORTED = typeof window !== 'undefined' && 'speechSynthesis' in window
// WebKit — every browser on iOS/iPadOS, plus desktop Safari — needs different
// speechSynthesis handling than Chromium: the pause/resume keep-alive below is
// a Chromium workaround that HANGS WebKit, and WebKit can accept a speak() call
// and then do nothing at all (no start, no end, no error). Both are handled in
// speakWithBrowser; this is the one place the platform is detected.
const IS_WEBKIT_SPEECH = (() => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  // iPadOS reports a Macintosh UA, so touch points disambiguate it.
  const isIOS = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1)
  const isDesktopSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|CriOS|FxiOS/.test(ua)
  return isIOS || isDesktopSafari
})()
const speechListeners = new Set<(id: string | null, paused: boolean) => void>()
// Playback position feed for the transport's progress bar. Notified from the
// element's timeupdate (~4Hz); only the ACTIVE message's button subscribes.
const progressListeners = new Set<(position: number, duration: number) => void>()
let currentSpeakingId: string | null = null
let currentPaused = false
let currentStop: (() => void) | null = null
// Neural playback plays through a real <audio> element (not Web Audio):
// media elements are the only thing iOS keeps playing when the screen locks
// or the PWA goes to the background, and they give pause/seek and lock-screen
// (Media Session) controls for free. Chunks are still fetched/decoded through
// the Web Audio API — decoding is what the shared context is for now — and
// assembled into a single WAV blob, so seeking backward always works and
// there are no per-chunk seams once assembled. The element and its blob URL
// are tracked module-level so Stop can halt them from anywhere.
let audioCtx: AudioContext | null = null
let currentAudioEl: HTMLAudioElement | null = null
let currentBlobUrl: string | null = null
// Seek routing for the active element playback. Set by speak() (which owns
// the assembly state a seek may need to extend into) and consulted by the
// module-level seek helpers that the transport and Media Session call.
let currentPlayback: { seekTo: (seconds: number) => void } | null = null
// Chromium stops a speechSynthesis utterance after ~15s unless nudged; this
// pause/resume keep-alive ticks while the browser voice is speaking.
let browserKeepAlive: ReturnType<typeof setInterval> | null = null

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  if (!audioCtx) audioCtx = new Ctor()
  return audioCtx
}

function setSpeechState(id: string | null, paused = false) {
  currentSpeakingId = id
  currentPaused = paused
  speechListeners.forEach(fn => fn(id, paused))
}

/** Synchronous check of the module playback registry. True from the moment a
 *  speak() registers itself (before the React `speaking`/`loading` state has
 *  propagated through a render) until playback fully ends, so queue pumps can
 *  rely on it to never double-start during that state-update gap. */
export function isSpeechActive(): boolean {
  return currentSpeakingId !== null
}

/** Tear down the active <audio> element and its blob URL. */
function stopAudioElement() {
  currentPlayback = null
  if (currentAudioEl) {
    const el = currentAudioEl
    currentAudioEl = null
    el.onended = null
    el.onplay = null
    el.onpause = null
    el.ontimeupdate = null
    try { el.pause() } catch { /* ignore */ }
    el.removeAttribute('src')
  }
  if (currentBlobUrl) {
    URL.revokeObjectURL(currentBlobUrl)
    currentBlobUrl = null
  }
  clearMediaSession()
}

// ── Media Session: lock-screen / control-center transport controls ──────────
// Registered while the neural voice is playing so the reply behaves like any
// media: play/pause and ±10s from the lock screen keep working with the
// screen off. Handlers drive the live element through the module refs, so
// they never go stale across blob extensions.
export const SEEK_STEP_SECONDS = 10

function setupMediaSession() {
  if (!('mediaSession' in navigator)) return
  const ms = navigator.mediaSession
  try {
    ms.metadata = new MediaMetadata({ title: 'Claude reply', artist: 'Code Concierge' })
    ms.setActionHandler('play', () => resumeCurrentAudio())
    ms.setActionHandler('pause', () => pauseCurrentAudio())
    ms.setActionHandler('stop', () => { stopAllSpeech(); setSpeechState(null) })
    ms.setActionHandler('seekbackward', (d) => seekCurrentAudioBy(-(d.seekOffset || SEEK_STEP_SECONDS)))
    ms.setActionHandler('seekforward', (d) => seekCurrentAudioBy(d.seekOffset || SEEK_STEP_SECONDS))
    ms.setActionHandler('seekto', (d) => {
      if (typeof d.seekTime === 'number') seekCurrentAudioTo(d.seekTime)
    })
  } catch { /* partial support — whatever registered still works */ }
}

function clearMediaSession() {
  if (!('mediaSession' in navigator)) return
  const ms = navigator.mediaSession
  try {
    ms.metadata = null
    for (const action of ['play', 'pause', 'stop', 'seekbackward', 'seekforward', 'seekto'] as MediaSessionAction[]) {
      ms.setActionHandler(action, null)
    }
  } catch { /* ignore */ }
}

function pauseCurrentAudio() {
  currentAudioEl?.pause()
}

function resumeCurrentAudio() {
  currentAudioEl?.play().catch(() => { /* interrupted by stop */ })
}

function seekCurrentAudioBy(seconds: number) {
  const el = currentAudioEl
  if (!el) return
  seekCurrentAudioTo(el.currentTime + seconds)
}

function seekCurrentAudioTo(seconds: number) {
  // Route through the active playback: it knows the full decoded timeline and
  // can extend the playable blob when the target lies beyond its edge.
  if (currentPlayback) {
    currentPlayback.seekTo(seconds)
    return
  }
  const el = currentAudioEl
  if (!el) return
  const max = Number.isFinite(el.duration) ? el.duration : Infinity
  el.currentTime = Math.min(Math.max(0, seconds), Math.max(0, max - 0.05))
}

// ── PCM → WAV assembly ──────────────────────────────────────────────────────
// The decoded chunk AudioBuffers (all from the one shared context, so one
// sample rate) are re-encoded into a single 16-bit PCM WAV blob the element
// can play. Cheap: a reply's audio is a few MB and encoding is one pass.
function buffersToWavBlob(buffers: AudioBuffer[]): Blob {
  const sampleRate = buffers[0].sampleRate
  const channels = buffers[0].numberOfChannels
  let frames = 0
  for (const b of buffers) frames += b.length
  const dataBytes = frames * channels * 2
  const buf = new ArrayBuffer(44 + dataBytes)
  const view = new DataView(buf)
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i))
  }
  writeStr(0, 'RIFF')
  view.setUint32(4, 36 + dataBytes, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true) // PCM
  view.setUint16(22, channels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * channels * 2, true)
  view.setUint16(32, channels * 2, true)
  view.setUint16(34, 16, true)
  writeStr(36, 'data')
  view.setUint32(40, dataBytes, true)
  let offset = 44
  for (const b of buffers) {
    for (let frame = 0; frame < b.length; frame++) {
      for (let ch = 0; ch < channels; ch++) {
        const sample = b.getChannelData(Math.min(ch, b.numberOfChannels - 1))[frame]
        const clamped = Math.max(-1, Math.min(1, sample))
        view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
        offset += 2
      }
    }
  }
  return new Blob([buf], { type: 'audio/wav' })
}

// A ~50ms silent WAV data URI. Playing it synchronously inside the Listen
// click "blesses" the element under iOS autoplay policy, so the real src can
// start later from async code (after synthesis) without a fresh gesture.
let silentWavUri: string | null = null
function getSilentWavUri(): string {
  if (silentWavUri) return silentWavUri
  const sampleRate = 8000
  const frames = 400
  const buf = new ArrayBuffer(44 + frames * 2)
  const view = new DataView(buf)
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i))
  }
  writeStr(0, 'RIFF'); view.setUint32(4, 36 + frames * 2, true); writeStr(8, 'WAVE')
  writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true)
  view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true)
  writeStr(36, 'data'); view.setUint32(40, frames * 2, true)
  let bin = ''
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  silentWavUri = `data:audio/wav;base64,${btoa(bin)}`
  return silentWavUri
}


// ── Generic PCM WAV synthesis (earcons + keep-alive) ────────────────────────
// Mono 16-bit WAV as a data URI, from an Int16Array. Same header layout as
// getSilentWavUri above; used for the voice-mode earcons and keep-alive tone.
function pcmWavUri(samples: Int16Array, sampleRate: number): string {
  const buf = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buf)
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i))
  }
  writeStr(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); writeStr(8, 'WAVE')
  writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true)
  view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true)
  writeStr(36, 'data'); view.setUint32(40, samples.length * 2, true)
  for (let i = 0; i < samples.length; i++) view.setInt16(44 + i * 2, samples[i], true)
  let bin = ''
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return `data:audio/wav;base64,${btoa(bin)}`
}

/** Sine tones in sequence (freq 0 = silence), short edge fades to avoid clicks. */
function toneWavUri(segments: { freq: number; ms: number; amp?: number }[], amp = 0.22, rate = 16000): string {
  let total = 0
  for (const s of segments) total += Math.round((s.ms / 1000) * rate)
  const samples = new Int16Array(total)
  let pos = 0
  for (const s of segments) {
    const n = Math.round((s.ms / 1000) * rate)
    if (s.freq === 0) { pos += n; continue }
    const segAmp = s.amp ?? amp
    const fade = Math.min(Math.round(rate * 0.008), Math.floor(n / 2))
    for (let i = 0; i < n; i++) {
      let a = segAmp
      if (i < fade) a *= i / fade
      else if (i > n - fade) a *= (n - i) / fade
      samples[pos++] = Math.round(Math.sin((2 * Math.PI * s.freq * i) / rate) * a * 0x7fff)
    }
  }
  return pcmWavUri(samples, rate)
}

// ── Voice-mode audio session infrastructure ─────────────────────────────────
// iOS only lets audio start OUTSIDE a user gesture on an element that already
// played INSIDE one, and it SUSPENDS a locked-screen page the moment no audio
// is playing — freezing JS, the mic, and any pending synthesis. Voice mode
// therefore uses three long-lived elements, all pre-unlocked ("blessed")
// inside the toggle's click gesture:
//   blessedAudioEl — carries reply playback (speak() prefers it over a fresh
//     element so auto-speak from an effect can start with the screen off)
//   keepAliveEl    — loops a near-silent tone (±1 LSB, inaudible) for the
//     whole voice session so the page keeps running through screen lock
//   earconEl       — plays the short stage-transition cues
let blessedAudioEl: HTMLAudioElement | null = null
let keepAliveEl: HTMLAudioElement | null = null
let earconEl: HTMLAudioElement | null = null
let progressEl: HTMLAudioElement | null = null

export function blessAutoplayElement() {
  if (typeof Audio === 'undefined') return
  if (!blessedAudioEl) blessedAudioEl = new Audio()
  if (!keepAliveEl) keepAliveEl = new Audio()
  if (!earconEl) earconEl = new Audio()
  if (!progressEl) progressEl = new Audio()
  for (const el of [blessedAudioEl, keepAliveEl, earconEl, progressEl]) {
    if (currentAudioEl === el) continue  // already playing real audio
    el.loop = false
    el.src = getSilentWavUri()
    el.play().catch(() => { /* not a gesture after all — harmless */ })
  }
}

// Near-silent keep-alive: a few seconds of ±1 LSB "noise" — mathematically
// non-silent (some platforms detect and stop pure-silence loops) but far
// below audibility.
let keepAliveUri: string | null = null
function getKeepAliveUri(): string {
  if (keepAliveUri) return keepAliveUri
  const rate = 8000
  const samples = new Int16Array(rate * 3)
  for (let i = 0; i < samples.length; i++) samples[i] = i % 2 === 0 ? 1 : -1
  keepAliveUri = pcmWavUri(samples, rate)
  return keepAliveUri
}

export function startVoiceKeepAlive() {
  if (typeof Audio === 'undefined') return
  if (!keepAliveEl) keepAliveEl = new Audio()
  keepAliveEl.loop = true
  keepAliveEl.src = getKeepAliveUri()
  keepAliveEl.play().catch(() => { /* not blessed yet — voice toggle re-tries */ })
}

export function stopVoiceKeepAlive() {
  if (!keepAliveEl) return
  try { keepAliveEl.pause() } catch { /* ignore */ }
  keepAliveEl.removeAttribute('src')
  // load() aborts a still-PENDING play() — a pause() landing before iOS
  // commits the play is a no-op, and the loop would start (and keep the audio
  // session alive) AFTER this stop.
  try { keepAliveEl.load() } catch { /* ignore */ }
}

// Ambient progress loops for eyes-free use — an audible heartbeat that says
// "still alive" with the screen off. 'working' = one soft low blip every
// ~2.5s while the agent works or the reply is being synthesized;
// 'recording' = two quick lighter ticks while the mic is capturing speech.
// Amplitudes are baked into the PCM (iOS ignores element.volume).
const progressUris: Partial<Record<'working' | 'recording', string>> = {}
let activeProgressKind: 'working' | 'recording' | null = null
// Bumped on every start/stop so a pending play() can detect it was superseded.
let progressGen = 0

export function startProgressLoop(kind: 'working' | 'recording') {
  if (typeof Audio === 'undefined') return
  if (activeProgressKind === kind) return
  if (!progressUris[kind]) {
    progressUris[kind] =
      kind === 'working'
        ? toneWavUri([{ freq: 392, ms: 70, amp: 0.07 }, { freq: 0, ms: 2400 }])
        : toneWavUri([
            { freq: 1180, ms: 35, amp: 0.05 }, { freq: 0, ms: 140 },
            { freq: 1180, ms: 35, amp: 0.05 }, { freq: 0, ms: 2300 },
          ])
  }
  if (!progressEl) progressEl = new Audio()
  activeProgressKind = kind
  progressEl.loop = true
  progressEl.src = progressUris[kind]!
  const gen = ++progressGen
  progressEl.play().then(() => {
    // iOS commits play() asynchronously. If a stop landed while this was
    // pending, its pause() was a no-op and the loop starts NOW — audibly
    // ticking forever after voice mode was turned off. Kill it on arrival.
    if (gen !== progressGen) {
      try { progressEl?.pause() } catch { /* ignore */ }
    }
  }).catch(() => { /* unblessed/backgrounded — best-effort */ })
}

export function stopProgressLoop() {
  progressGen++
  activeProgressKind = null
  if (!progressEl) return
  try { progressEl.pause() } catch { /* ignore */ }
  progressEl.removeAttribute('src')
  // Abort any still-pending play() (see startProgressLoop) and flush the
  // buffered loop so nothing can resume it.
  try { progressEl.load() } catch { /* ignore */ }
}

// Stage-transition earcons for eyes-free use: 'listen' = rising two-tone
// (your turn — speak), 'send' = single blip (message sent, agent working),
// 'error' = low buzz (mic/playback problem).
const earconUris: Partial<Record<'listen' | 'send' | 'error', string>> = {}
export function playEarcon(kind: 'listen' | 'send' | 'error') {
  if (typeof Audio === 'undefined') return
  if (!earconUris[kind]) {
    earconUris[kind] =
      kind === 'listen' ? toneWavUri([{ freq: 620, ms: 90 }, { freq: 930, ms: 110 }])
      : kind === 'send' ? toneWavUri([{ freq: 880, ms: 120 }])
      : toneWavUri([{ freq: 220, ms: 200 }], 0.3)
  }
  if (!earconEl) earconEl = new Audio()
  earconEl.loop = false
  earconEl.src = earconUris[kind]!
  earconEl.play().catch(() => { /* unblessed/backgrounded — cue is best-effort */ })
}

function startBrowserKeepAlive() {
  // Chromium-only. On WebKit the same pause()/resume() is destructive: pause()
  // stops the reading WITHOUT firing 'end', and resume() frequently never
  // takes — leaving speechSynthesis stuck `paused`, a state where every later
  // utterance queues silently forever. That turns "reply is a bit long" into
  // "the Listen button never comes back and nothing ever speaks again", which
  // is the opposite of what the keep-alive is for. WebKit doesn't truncate at
  // ~15s anyway, and our 200-char chunks stay well inside any limit.
  if (!SPEECH_SYNTH_SUPPORTED || IS_WEBKIT_SPEECH || browserKeepAlive) return
  browserKeepAlive = setInterval(() => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause()
      window.speechSynthesis.resume()
    }
  }, 10000)
}

function stopBrowserKeepAlive() {
  if (browserKeepAlive) { clearInterval(browserKeepAlive); browserKeepAlive = null }
}

// Watchdog timings for the browser voice. speechSynthesis is allowed to accept
// speak() and then never call back at all — no 'start', no 'end', no 'error' —
// which is how a tap on Listen turned into a button parked in the stop state
// with permanent silence. Nothing here is a normal-path delay: a working engine
// starts in well under a second and the watchdog is cleared the moment it does.
const SPEECH_WATCHDOG_TICK_MS = 400
// WebKit drops an utterance issued in the same tick as cancel(); re-issue once.
const SPEECH_START_RETRY_MS = 1200
// Still nothing after this → the local engine is not going to speak this reply.
const SPEECH_START_FAIL_MS = 3500

/** Stop everything currently playing: an in-flight chunk pipeline, neural
 *  audio, and the browser voice. Safe to call when nothing is active. */
export function stopAllSpeech() {
  if (currentStop) { const s = currentStop; currentStop = null; s() }
  stopAudioElement()
  stopBrowserKeepAlive()
  if (SPEECH_SYNTH_SUPPORTED) window.speechSynthesis.cancel()
}

// Client-side cache of already-decoded chunk audio, keyed by voice+text. Once a
// reply has been synthesized, listening to it again (or re-playing the same
// sentence) reuses the decoded AudioBuffer with ZERO network and ZERO Gemini
// synthesis — replay is instant and free. AudioBuffers are immutable and can be
// re-bound to a fresh source node each play, so sharing them is safe. Bounded by
// total decoded bytes with LRU eviction so the tab can't grow without limit.
const AUDIO_CACHE_MAX_BYTES = 40 * 1024 * 1024  // ~40 MB of decoded PCM
const audioBufferCache = new Map<string, AudioBuffer>()  // insertion order = LRU
let audioBufferCacheBytes = 0
const bufferBytes = (b: AudioBuffer) => b.length * b.numberOfChannels * 4  // Float32
function audioCacheGet(key: string): AudioBuffer | undefined {
  const b = audioBufferCache.get(key)
  if (b) { audioBufferCache.delete(key); audioBufferCache.set(key, b) }  // bump to MRU
  return b
}
function audioCachePut(key: string, b: AudioBuffer) {
  if (audioBufferCache.has(key)) return
  audioBufferCache.set(key, b)
  audioBufferCacheBytes += bufferBytes(b)
  while (audioBufferCacheBytes > AUDIO_CACHE_MAX_BYTES && audioBufferCache.size > 1) {
    const oldest = audioBufferCache.keys().next().value as string
    const evicted = audioBufferCache.get(oldest)!
    audioBufferCache.delete(oldest)
    audioBufferCacheBytes -= bufferBytes(evicted)
  }
}

// Ceilings on how long we wait for one chunk's synthesis. Two tiers, because
// the cost of giving up differs by position:
//  - FIRST chunk: nothing is playing yet, so a hang means dead air with no
//    feedback — time out fast and fall back to the browser voice.
//  - LATER chunks: audio is already playing (or the watchdog is padding
//    silence), so waiting longer is nearly free — while giving up DROPS the
//    chunk's text from the reading. The backend's own Gemini call is bounded
//    at 20s (it 504s past that), so 25s means we nearly always get a
//    definitive backend answer instead of aborting a synthesis that was
//    about to succeed. Cutting a healthy-but-slow request off early is what
//    silently skipped whole paragraphs mid-reply.
const FIRST_CHUNK_TIMEOUT_MS = 12000
const CHUNK_TIMEOUT_MS = 25000

/** Fetch one chunk of synthesized audio and decode it to an AudioBuffer.
 *  `voice` (when set) selects the per-user Gemini voice; the backend validates
 *  it. Returns a cached decoded buffer when the same voice+text was played
 *  before (no refetch, no re-synthesis). Bounded by CHUNK_TIMEOUT_MS so one slow
 *  synthesis can't stall playback. Decoding up front lets the caller schedule
 *  chunks gaplessly on the audio clock instead of swapping <audio>. */
async function fetchChunkBuffer(ctx: AudioContext, apiBase: string, text: string, voice: string | undefined, signal: AbortSignal, timeoutMs = CHUNK_TIMEOUT_MS): Promise<AudioBuffer> {
  const cacheKey = `${voice ?? ''}\n${text}`
  const cached = audioCacheGet(cacheKey)
  if (cached) return cached
  // Per-chunk abort: fires on the playback-wide signal OR our own timeout, so a
  // slow chunk aborts on its own without cancelling sibling chunks in flight.
  const ctl = new AbortController()
  const onParentAbort = () => ctl.abort()
  if (signal.aborted) ctl.abort()
  else signal.addEventListener('abort', onParentAbort, { once: true })
  const timer = setTimeout(() => ctl.abort(), timeoutMs)
  const startedAt = (typeof performance !== 'undefined' ? performance.now() : 0)
  try {
    const resp = await fetch(`${apiBase}/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(voice ? { text, voice } : { text }),
      signal: ctl.signal,
    })
    if (!resp.ok) throw new Error(`tts ${resp.status}`)
    const bytes = await resp.arrayBuffer()
    // decodeAudioData detaches the buffer; we only use it once.
    const buffer = await ctx.decodeAudioData(bytes)
    audioCachePut(cacheKey, buffer)
    const took = (typeof performance !== 'undefined' ? performance.now() - startedAt : 0)
    if (took > 6000) console.warn(`[claude-panel] slow TTS chunk: ${(took / 1000).toFixed(1)}s for ${text.length} chars`)
    return buffer
  } finally {
    clearTimeout(timer)
    signal.removeEventListener('abort', onParentAbort)
  }
}

/**
 * The id of whatever is currently being read, or null.
 *
 * `useSpeech(id)` answers "is MY message playing?", which is the right question
 * for a button attached to one message. A persistent transport needs the other
 * direction — "what is playing, wherever it came from?" — so it can follow the
 * audio instead of being pinned to a bubble that scrolls out of view.
 */
export function useCurrentSpeechId(): string | null {
  const [activeId, setActiveId] = useState<string | null>(currentSpeakingId)
  useEffect(() => {
    const listener = (activeIdNow: string | null) => setActiveId(activeIdNow)
    speechListeners.add(listener)
    // Re-sync on mount: playback survives unmounts (it lives at module level),
    // so a bar mounting mid-reading must adopt the reading already in progress.
    setActiveId(currentSpeakingId)
    return () => { speechListeners.delete(listener) }
  }, [])
  return activeId
}

export function useSpeech(id: string) {
  const [activeId, setActiveId] = useState<string | null>(currentSpeakingId)
  const [paused, setPaused] = useState(currentPaused)
  // Whether the active playback runs through the <audio> element (pause/seek
  // capable). The browser-voice fallback has no transport — stop only.
  const [hasTransport, setHasTransport] = useState(currentAudioEl !== null)
  const [loading, setLoading] = useState(false)
  // Brief "playback failed" flash on the button. Set when synthesis yields no
  // audio the user can hear — the neural voice failed and no usable fallback
  // played, or repeated failures cut the reply short. Without it a failed
  // Listen just spins and silently returns to idle, leaving the user unsure
  // whether it worked. Auto-clears after a few seconds; clicking again retries.
  const [error, setError] = useState(false)
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flashError = useCallback(() => {
    setError(true)
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
    errorTimerRef.current = setTimeout(() => setError(false), 3000)
  }, [])
  // Effective TTS settings (admin default + per-user override, resolved server
  // side). "browser" = local speechSynthesis (instant, no network); "gemini" =
  // neural backend voice. Defaults to gemini before settings have loaded.
  const settings = useSettingsOptional()?.settings
  const ttsEngine = settings?.tts?.engine ?? 'gemini'
  const ttsVoice = settings?.tts?.voice

  useEffect(() => {
    const listener = (activeIdNow: string | null, pausedNow: boolean) => {
      setActiveId(activeIdNow)
      setPaused(pausedNow)
      setHasTransport(currentAudioEl !== null)
    }
    speechListeners.add(listener)
    return () => {
      speechListeners.delete(listener)
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current)
      // Deliberately NOT stopping playback on unmount: the audio element and
      // its controls live at module level, so the reply keeps playing when
      // the user switches tabs (Files, terminal) or backgrounds the app —
      // that background continuity is the point. Remounting re-syncs the
      // button from currentSpeakingId; lock-screen controls always work.
    }
  }, [id])

  const speaking = activeId === id

  const stop = useCallback(() => {
    stopAllSpeech()
    if (currentSpeakingId === id) setSpeechState(null)
    setLoading(false)
  }, [id])

  const pause = useCallback(() => pauseCurrentAudio(), [])
  const resume = useCallback(() => resumeCurrentAudio(), [])
  const seekBy = useCallback((seconds: number) => seekCurrentAudioBy(seconds), [])
  const seekTo = useCallback((seconds: number) => seekCurrentAudioTo(seconds), [])

  // Progress feed for the transport bar — subscribed only while THIS message
  // is the one playing through the element, so idle buttons never re-render
  // on timeupdate ticks.
  const [progress, setProgress] = useState({ position: 0, duration: 0 })
  useEffect(() => {
    if (!speaking || !hasTransport) return
    const fn = (position: number, duration: number) => setProgress({ position, duration })
    progressListeners.add(fn)
    return () => { progressListeners.delete(fn) }
  }, [speaking, hasTransport])

  // Browser speechSynthesis path (the "browser" engine, and the fallback when
  // the neural endpoint fails). Speaks sentence-sized utterances in sequence —
  // Chromium truncates a single long utterance after ~15s, so chunking plus the
  // keep-alive ticker keeps long replies playing to the end.
  //
  // Every exit is watchdogged, because "accepted the utterance and then did
  // nothing" is a real state this API reaches (WebKit after a same-tick
  // cancel(), any engine after an OS-level audio interruption). Treating that
  // as "still reading" is what left the button in its stop state with silence
  // behind it — and, in voice mode, wedged the phase machine in 'speaking' so
  // the mic never re-armed. `onUnavailable` lets the caller hand the reply to
  // the neural voice when the local engine never gets a word out.
  const speakWithBrowser = useCallback((text: string, onUnavailable?: () => void) => {
    // Nothing was heard: hand off to the fallback, or fail visibly. Either way
    // the button returns to idle instead of pretending to read.
    const nothingSpoken = () => {
      if (currentSpeakingId === id) setSpeechState(null)
      if (onUnavailable) onUnavailable()
      else flashError()
    }
    if (!SPEECH_SYNTH_SUPPORTED) { nothingSpoken(); return }
    const synth = window.speechSynthesis
    synth.cancel()
    // A keep-alive tick or an OS interruption can leave the queue stuck
    // `paused` — a state where speak() enqueues and nothing ever plays again
    // for the life of the page. Always un-stick before speaking.
    if (synth.paused) { try { synth.resume() } catch { /* not supported */ } }
    const chunks = splitIntoSpeechChunks(text, 200)
    if (!chunks.length) { if (currentSpeakingId === id) setSpeechState(null); return }
    setSpeechState(id)
    startBrowserKeepAlive()

    let watchdog: ReturnType<typeof setInterval> | null = null
    const clearWatchdog = () => { if (watchdog) { clearInterval(watchdog); watchdog = null } }
    // Register a stop handle so the module registry always reflects live
    // playback. This matters when speakWithBrowser runs as the neural fallback:
    // the neural path's `finally` clears currentStop only when it still points
    // at its own handle, so installing ours here keeps the browser voice
    // stoppable instead of leaving currentStop null mid-sentence.
    const myStop = () => { clearWatchdog(); stopBrowserKeepAlive(); synth.cancel() }
    currentStop = myStop
    let i = 0
    setError(false)

    // Drop our claim on the shared engine — but only while we still own it, so
    // a late event from a superseded reading can't stop the one that replaced it.
    const release = () => {
      clearWatchdog()
      if (currentStop === myStop) { currentStop = null; stopBrowserKeepAlive() }
    }
    // The engine went unresponsive. Before a single word landed the reply is
    // still fully recoverable through the fallback; mid-reply it isn't, so end
    // cleanly with the "failed, tap to retry" flash rather than re-reading from
    // the top in a different voice.
    const abandon = () => {
      clearWatchdog()
      if (currentSpeakingId !== id) { release(); return }
      release()
      synth.cancel()
      if (i <= 1) { nothingSpoken(); return }
      setSpeechState(null)
      flashError()
    }

    const speakNext = () => {
      // Stopped (id cleared/changed) or finished — tear down and clear state.
      if (currentSpeakingId !== id || i >= chunks.length) {
        release()
        if (currentSpeakingId === id && i >= chunks.length) setSpeechState(null)
        return
      }
      const chunk = chunks[i]
      i++
      let started = false
      let settled = false
      let retried = false
      let quietTicks = 0
      const startedAt = Date.now()
      const settle = (next: () => void) => {
        if (settled) return
        settled = true
        clearWatchdog()
        next()
      }
      const issue = () => {
        const utterance = new SpeechSynthesisUtterance(chunk)
        utterance.onstart = () => { started = true }
        utterance.onend = () => settle(speakNext)
        utterance.onerror = (event) => settle(() => {
          // Our own stop()/supersede — the teardown that cancelled us already ran.
          if (event.error === 'interrupted' || event.error === 'canceled') { release(); return }
          abandon()
        })
        synth.speak(utterance)
      }
      issue()

      clearWatchdog()
      watchdog = setInterval(() => {
        if (settled) { clearWatchdog(); return }
        if (currentSpeakingId !== id) { release(); return }
        const elapsed = Date.now() - startedAt
        const quiet = !synth.speaking && !synth.pending
        // Some engines never fire 'start'; the queue reporting itself busy is
        // just as good a sign that this utterance is under way.
        if (!started && !quiet) started = true
        if (!started) {
          if (elapsed >= SPEECH_START_FAIL_MS) { settle(abandon); return }
          if (!retried && elapsed >= SPEECH_START_RETRY_MS) { retried = true; issue() }
          return
        }
        // Started, then the queue fell silent with no 'end' event. Move on so
        // the rest of the reply is still read instead of stalling here forever.
        quietTicks = quiet ? quietTicks + 1 : 0
        if (quietTicks >= 2) settle(speakNext)
      }, SPEECH_WATCHDOG_TICK_MS)
    }
    speakNext()
  }, [id, flashError])

  // Re-entry point for the browser→neural fallback: speak() can't depend on
  // itself, so the current closure is parked here for the watchdog to call.
  const speakRef = useRef<(text: string, forceEngine?: 'gemini') => void>(() => {})

  // Neural TTS: synthesize the reply in sentence chunks so the first (small)
  // chunk starts almost immediately while the rest is fetched ahead. Decoded
  // chunks are assembled into a single WAV blob played through one <audio>
  // element: media playback survives screen-lock/backgrounding on iOS (Web
  // Audio does not), and gives pause/seek plus lock-screen controls. The blob
  // is extended at chunk boundaries as synthesis lands, so playback starts on
  // the first sentence and seeking backward always has the full audio.
  // Falls back to the browser voice on any failure.
  const speak = useCallback(async (text: string, forceEngine?: 'gemini') => {
    if (!text.trim()) return
    stopAllSpeech()
    setError(false)

    // Browser engine: speak locally and skip the neural backend entirely. When
    // the local engine never gets a word out, fall back to the neural voice for
    // this reply — the mirror of the neural path's fallback below, and the
    // difference between "my replies stopped talking" and a slower first
    // sentence. `forceEngine` is how that re-entry skips this branch.
    if ((forceEngine ?? ttsEngine) === 'browser') {
      speakWithBrowser(text, () => {
        console.warn('[claude-panel] browser voice produced no audio — falling back to the neural voice')
        speakRef.current(text, 'gemini')
      })
      return
    }

    // Web Audio is still needed to DECODE the chunk WAVs; without it (very
    // old browsers) fall back to the browser voice.
    const ctx = getAudioContext()
    if (!ctx) { speakWithBrowser(text); return }

    // Create and start the element SYNCHRONOUSLY inside the Listen click:
    // playing a looped silent clip under the user gesture blesses the element
    // for iOS autoplay policy, so the real audio (which arrives from async
    // synthesis) can start and keep playing with the screen off.
    // Prefer the voice-mode blessed element (already gesture-unlocked) so
    // auto-speak from an effect can start playback; a gesture-driven Listen
    // click works identically through it.
    const el = blessedAudioEl ?? new Audio()
    el.preload = 'auto'
    el.loop = true
    el.src = getSilentWavUri()
    el.play().catch(() => { /* autoplay refusal — real play retries below */ })
    currentAudioEl = el

    // Register this playback as the active one BEFORE any await. A second Listen
    // click that lands while we're awaiting synthesis then aborts us cleanly
    // through currentStop, instead of racing us and leaving two playbacks
    // active at once with one orphaned stop handle.
    setSpeechState(id)
    setLoading(true)

    const apiBase = resolveApiBase(window.location.pathname)
    // Chunk sizing leans SMALL on purpose. A smaller chunk synthesizes faster, so
    // it's far less likely to exceed CHUNK_TIMEOUT_MS and get dropped mid-reply —
    // that drop is exactly what silently skipped a whole paragraph. It also makes
    // any wait shorter. The cost is more chunk boundaries (each is an independent
    // Gemini synthesis with fresh prosody, so tone can shift a touch between them)
    // and more requests. For a read-aloud feature, reliably reading the WHOLE
    // reply beats marginally smoother prosody, so we accept that trade. 300 lands
    // that balance; the first chunk stays small (120) via the ramp so
    // time-to-first-audio is unaffected.
    const chunks = splitIntoSpeechChunks(text, 300, 120)
    const controller = new AbortController()
    let stopped = false
    // Set when the reply is cut short by repeated synthesis failures (or an
    // unexpected error) so the button can flash "failed" once we tear down.
    let playbackFailed = false
    const myStop = () => { stopped = true; controller.abort() }
    currentStop = myStop

    // Assembly state: decoded buffers in play order, and how many of them the
    // element's CURRENT blob includes. The element normally swaps src at a
    // chunk boundary (its 'ended', or the very first chunk while silence
    // loops), resuming at the exact end of the previous blob — so the seam
    // sits on a sentence boundary where prosody already shifts. A seek past
    // the playable edge forces an immediate extension instead (the user asked
    // to jump; a mid-word seam is fine there).
    //
    // The progress bar and lock screen show `totalDecoded` — the duration of
    // EVERYTHING synthesized so far, which races well ahead of playback — not
    // the current blob. Without that the visible total crawled upward one
    // chunk at a time exactly when the playhead hit each boundary.
    const assembled: AudioBuffer[] = []
    let includedChunks = 0
    let includedDuration = 0
    let totalDecoded = 0
    let pendingSeek: number | null = null
    let pipelineDone = false
    let swapping = false
    let finishedResolve!: () => void
    const finished = new Promise<void>((res) => { finishedResolve = res })
    controller.signal.addEventListener('abort', () => finishedResolve(), { once: true })

    const extendOrFinish = () => {
      if (stopped || currentAudioEl !== el) return
      if (assembled.length > includedChunks) {
        swapping = true
        const resumeAt = pendingSeek !== null
          ? pendingSeek
          : includedChunks === 0
            ? 0 // first swap replaces the blessed silence loop — start of the reply
            : el.ended
              ? includedDuration
              : el.currentTime
        pendingSeek = null
        includedChunks = assembled.length
        includedDuration = assembled.reduce((s, b) => s + b.duration, 0)
        const url = URL.createObjectURL(buffersToWavBlob(assembled))
        if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl)
        currentBlobUrl = url
        el.loop = false
        el.src = url
        // currentTime is only settable once metadata is in; blob URLs load
        // from memory so this is near-instant.
        el.addEventListener('loadedmetadata', () => {
          try { el.currentTime = Math.max(0, Math.min(resumeAt, includedDuration - 0.05)) } catch { /* start of clip */ }
          el.play().catch(() => { /* stopped mid-swap */ })
          swapping = false
        }, { once: true })
        el.load()
      } else if (pendingSeek !== null) {
        // Seek within the already-playable audio (e.g. backward after the
        // element ran dry waiting for synthesis) — just jump and keep going.
        const target = Math.max(0, Math.min(pendingSeek, includedDuration - 0.05))
        pendingSeek = null
        try { el.currentTime = target } catch { /* not loaded yet */ }
        if (el.paused && !currentPaused) el.play().catch(() => { /* stopped */ })
      } else if (pipelineDone) {
        finishedResolve()
      }
      // else: synthesis fell behind — the fetch loop re-calls this when the
      // next chunk lands (the gap plays out as silence, same as before).
    }

    // Seek entry point for the transport/lock screen. Targets beyond the
    // playable edge (or an element sitting at 'ended') queue the position and
    // force an extension so the jump lands in already-decoded audio.
    currentPlayback = {
      seekTo: (seconds: number) => {
        if (currentAudioEl !== el || totalDecoded <= 0) return
        const target = Math.max(0, Math.min(seconds, totalDecoded - 0.05))
        if (!el.ended && target < includedDuration - 0.05) {
          try { el.currentTime = target } catch { /* not loaded yet */ }
          return
        }
        pendingSeek = target
        extendOrFinish()
      },
    }

    el.onended = extendOrFinish
    // Reflect real pauses/resumes (button, lock screen, headphones) in the UI
    // and the lock-screen state. Transient pauses from src swaps and the
    // pause that accompanies 'ended' are not user intent — skip those.
    el.onplay = () => {
      if (currentAudioEl !== el || currentSpeakingId !== id || swapping) return
      try { navigator.mediaSession.playbackState = 'playing' } catch { /* unsupported */ }
      setSpeechState(id, false)
    }
    el.onpause = () => {
      if (currentAudioEl !== el || currentSpeakingId !== id || swapping || el.ended) return
      try { navigator.mediaSession.playbackState = 'paused' } catch { /* unsupported */ }
      setSpeechState(id, true)
    }
    el.ontimeupdate = () => {
      if (currentAudioEl !== el || totalDecoded <= 0) return
      const position = Math.min(el.currentTime, totalDecoded)
      progressListeners.forEach((fn) => fn(position, totalDecoded))
      try {
        navigator.mediaSession.setPositionState({
          duration: totalDecoded,
          position,
          playbackRate: 1,
        })
      } catch { /* unsupported */ }
    }

    // Continuous-playback watchdog. The original design extended the blob
    // only at the element's 'ended' — a brief stop at every chunk boundary
    // where synthesis had fallen behind. With the screen LOCKED that stop is
    // fatal: the instant no audio is playing, iOS suspends the page (JS,
    // fetches, everything), so playback never resumes and the reply dies
    // mid-sentence. Two rules keep the audio session alive to the true end:
    //   1. Extend BEFORE the playhead reaches the edge (low watermark), not
    //      at 'ended' — the mid-play src swap costs a barely audible seam,
    //      which beats a stop.
    //   2. If synthesis is genuinely starved (nothing new decoded), append
    //      SILENCE and keep playing through the wait instead of stopping.
    const LOW_WATERMARK_S = 10
    const STARVE_PAD_S = 3
    let starvePad: AudioBuffer | null = null
    const watchdog = setInterval(() => {
      if (stopped || currentAudioEl !== el || swapping || currentPaused) return
      if (includedChunks === 0) return  // opener not landed — blessed silence still looping
      const remaining = includedDuration - el.currentTime
      if (assembled.length > includedChunks) {
        if (el.ended || remaining < LOW_WATERMARK_S) extendOrFinish()
      } else if (!pipelineDone && (el.ended || remaining < 2)) {
        starvePad = starvePad || ctx.createBuffer(
          assembled[0]?.numberOfChannels || 1,
          Math.round(ctx.sampleRate * STARVE_PAD_S),
          ctx.sampleRate,
        )
        assembled.push(starvePad)
        totalDecoded += starvePad.duration
        extendOrFinish()
      }
    }, 1000)
    controller.signal.addEventListener('abort', () => clearInterval(watchdog), { once: true })

    try {
      if (stopped || currentSpeakingId !== id) return

      // Fetch + decode a bounded window ahead of playback (LOOKAHEAD in flight).
      // Three ahead keeps the pipeline further in front of playback so a slow
      // synthesis doesn't starve the schedule (the audible ~5s pause), while
      // still capping concurrency so we don't hammer the rate limit. Paired with
      // the ramp-up chunk sizes, the early chunks are small enough to decode
      // before the short opener finishes.
      const LOOKAHEAD = 3
      const inflight: (Promise<AudioBuffer> | null)[] = new Array(chunks.length).fill(null)
      const fetchAt = (i: number) => {
        if (i < chunks.length && !inflight[i]) {
          const timeout = i === 0 ? FIRST_CHUNK_TIMEOUT_MS : CHUNK_TIMEOUT_MS
          const p = fetchChunkBuffer(ctx, apiBase, chunks[i], ttsVoice, controller.signal, timeout)
          p.catch(() => {})  // rejection is handled where the chunk is awaited
          inflight[i] = p
        }
      }
      for (let i = 0; i < LOOKAHEAD; i++) fetchAt(i)

      // Abort-aware delay, used to pace scheduling (below). Resolves early on Stop.
      const delay = (ms: number) => new Promise<void>((res) => {
        const t = setTimeout(res, ms)
        controller.signal.addEventListener('abort', () => { clearTimeout(t); res() }, { once: true })
      })

      let consecutiveFailures = 0
      for (let i = 0; i < chunks.length; i++) {
        let buffer: AudioBuffer | null = null
        try {
          buffer = await inflight[i] as AudioBuffer
        } catch {
          if (stopped || controller.signal.aborted) return
          // Retry ladder. The lookahead launches several chunks into the SAME
          // moment — one slow/rate-limited backend window fails them all at
          // once, and a single quick retry lands right back inside it (that's
          // how consecutive paragraphs got dropped together). So: one fast
          // retry for the transient blip, then a second after a real pause so
          // the window can pass. The later retry also tends to be nearly free:
          // the backend keeps synthesizing after a client-side timeout abort
          // and caches the result, so a re-request often just collects it.
          // First chunk keeps a single fast retry — nothing is playing yet,
          // and its fallback (browser voice) needs to kick in quickly.
          const backoffs = i === 0 ? [400] : [400, 3000]
          let err: unknown = null
          for (const backoff of backoffs) {
            await delay(backoff)
            if (stopped || controller.signal.aborted) return
            try {
              buffer = await fetchChunkBuffer(ctx, apiBase, chunks[i], ttsVoice, controller.signal)
              err = null
              break
            } catch (e) {
              if (stopped || controller.signal.aborted) return
              err = e
              buffer = null
            }
          }
          if (err !== null) {
            if (i === 0) {
              // Nothing has played yet → neural voice is unavailable. Fall back to
              // the browser voice for the whole reply (works on desktop; the total
              // -failure case).
              console.warn('[claude-panel] neural TTS failed on first chunk, using browser voice:', err)
              stopAudioElement()
              if (currentSpeakingId === id) speakWithBrowser(text)
              return
            }
            // Mid-reply failure: DON'T switch voices or stop. The browser voice is
            // unreliable/silent on iOS, and stopping loses the rest of the reply.
            // Skip this one sentence and keep the neural pipeline going — but mark
            // the playback as incomplete so the button ends in the "failed, tap to
            // retry" state. A retry replays from the cached chunks (instant) and
            // only re-synthesizes the dropped one, so the user can recover the
            // section that was silently skipped instead of never knowing it was.
            console.warn(`[claude-panel] skipping TTS chunk ${i}/${chunks.length} after failure:`, err)
            playbackFailed = true
            if (++consecutiveFailures >= 4) {
              // Several in a row → the service is down; end cleanly instead of
              // playing long silence to the end.
              console.warn('[claude-panel] too many consecutive TTS failures — stopping')
              break
            }
          }
        }
        if (stopped || currentSpeakingId !== id) return

        fetchAt(i + LOOKAHEAD)  // keep the look-ahead window full (even when skipping)
        if (!buffer) continue   // skipped chunk — nothing to append
        consecutiveFailures = 0

        assembled.push(buffer)
        totalDecoded += buffer.duration
        if (assembled.length === 1) {
          // First audio: swap the blessed silence loop for the real thing and
          // put the reply on the lock screen.
          setLoading(false)
          setupMediaSession()
          extendOrFinish()
        } else if (el.ended) {
          // Synthesis had fallen behind and the element ran out — extend now.
          extendOrFinish()
        }
        // Otherwise just accumulate; the element extends at its next 'ended'.
      }

      // All chunks fetched (or the failure cap broke the loop). Let the element
      // play out; extendOrFinish resolves `finished` at the true end (or the
      // stop/abort listener does).
      pipelineDone = true
      if (assembled.length === 0) {
        // Every chunk failed after the first succeeded elsewhere — nothing to
        // play. (First-chunk failure already fell back above.)
        finishedResolve()
      } else if (el.ended) {
        extendOrFinish()  // 'ended' fired before pipelineDone was set
      }
      await finished
      if (!stopped && currentSpeakingId === id) setSpeechState(null)
    } catch (err) {
      // Only a genuine error, not a user Stop (which aborts and returns above).
      if (!stopped && !controller.signal.aborted) {
        console.warn('[claude-panel] neural TTS playback error:', err)
        playbackFailed = true
      }
    } finally {
      clearInterval(watchdog)
      // Ownership: `currentStop` is replaced the moment another speak() takes
      // over, so this is the test for "am I still the live playback?".
      const stillOurs = currentStop === myStop
      // A superseded invocation must not clear the flags — or tear down the
      // element — belonging to the playback that replaced it.
      //
      // `currentAudioEl === el` is NOT sufficient on its own. On iOS every
      // playback reuses one gesture-blessed element, so `el` is the same
      // object for the old and new invocation, and the old one's teardown
      // would happily stop the new one's audio. That is what froze the
      // Summary jump: it re-reads under the same id, the superseded pipeline
      // reached this block a microtask later and nulled `currentAudioEl`,
      // leaving `currentSpeakingId` set. Transport visible, seek and pause
      // dead (no element behind them), only Stop working. It only bit once
      // the blessed element existed — before voice mode had ever been on,
      // each speak() got a fresh Audio and the identity check saved us.
      if (stillOurs) {
        currentStop = null
        setLoading(false)
        if (playbackFailed) flashError()
        // Natural end (not a user Stop, which already tore down): release the
        // element, blob URL and lock-screen session.
        if (currentAudioEl === el) stopAudioElement()
      }
    }
  }, [id, speakWithBrowser, ttsEngine, ttsVoice, flashError])

  useEffect(() => { speakRef.current = speak }, [speak])

  return { speaking, paused, hasTransport, loading, error, progress, speak, stop, pause, resume, seekBy, seekTo }
}


export function formatAudioTime(seconds: number): string {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
